import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Carregando from '@/components/Carregando';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import { baixarOriginal, ouvirImportacoes } from '@/services/importacaoService';

/**
 * Histórico de importações do Careflow (VA-2) — a conferência do gestor.
 *
 * A razão de existir: quem importa é o SDR, que ganha comissão sobre o que for
 * gravado. O arquivo original fica guardado e pode ser baixado daqui e comparado
 * com o relatório do Careflow. É esta tela que transforma "o valor veio de um
 * arquivo" em "e o arquivo pode ser conferido por outra pessoa".
 */

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const quando = (ts) => {
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : null;
  return d ? format(d, "d 'de' MMM',' HH:mm", { locale: ptBR }) : '—';
};

export default function ImportacoesPage() {
  const { clinicaId } = useAuth();
  const { clinica } = useLeads();
  const sistema = nomeDoSistema(clinica?.config);
  const [importacoes, setImportacoes] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirImportacoes(clinicaId, setImportacoes, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir importações:', e);
      setErro('Não foi possível carregar o histórico.');
    });
  }, [clinicaId]);

  if (erro) return <p className="erro">{erro}</p>;
  if (!importacoes) return <Carregando />;

  return (
    <section className="pagina">
      <header className="pagina-cabecalho">
        <h1>Importações do {sistema}</h1>
      </header>

      {importacoes.length === 0 ? (
        <p className="em-breve">Nenhuma importação ainda.</p>
      ) : (
        <div className="tabela-envolve">
          <table className="tabela">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Quem importou</th>
                <th>Situação</th>
                <th>Linhas no arquivo</th>
                <th>Lançadas</th>
                <th>Valor lançado</th>
                <th>Arquivo</th>
              </tr>
            </thead>
            <tbody>
              {importacoes.map((i) => (
                <tr key={i.id} className={i.status === 'confirmada' ? undefined : 'linha-apagada'}>
                  <td>{quando(i.confirmadaEm ?? i.criadoEm)}</td>
                  <td>{i.importadoPorNome ?? i.criadoPorNome ?? '—'}</td>
                  <td>
                    {/* "Conferida, não confirmada" é estado normal: alguém abriu o
                        arquivo, olhou e desistiu. Fica visível para o gestor
                        entender por que há arquivo sem lançamento. */}
                    {i.status === 'confirmada' ? 'Confirmada' : 'Conferida, não confirmada'}
                  </td>
                  <td>{i.linhasNoArquivo ?? '—'}</td>
                  <td>{i.gravadas ?? '—'}</td>
                  <td className="valor">{i.gravadas ? moeda(i.valorGravado) : '—'}</td>
                  <td>
                    {i.arquivoCaminho ? (
                      <button type="button" className="btn-texto" onClick={() => baixarOriginal(i)}>
                        Baixar original
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="secao-sub">
        O arquivo original de cada importação fica guardado. Baixe e compare com o relatório do
        {sistema} sempre que quiser conferir de onde veio um valor — os números lançados são
        lidos do arquivo pelo servidor, não digitados por quem importa.
      </p>
    </section>
  );
}
